/* tslint:disable max-line-length */
import { Component, OnInit, Renderer2, Inject } from '@angular/core';
import { ToolSettingsService } from 'src/app/service/tool-settings/tool-settings.service';
import { ToolSearchParams } from 'src/app/shared/models/toolSearchParams';
import { TableData } from 'src/app/shared/models/tableData';
import { ToolSnapshotService } from 'src/app/component/tools/tool-snapshot/tool-snapshot.service';
import { assignment, model } from '../tool-transfer/tool-transfer.functions';
import { DOCUMENT } from '@angular/common';
import { ToolPermissionsService } from 'src/app/service/auth/tool-permissions.service';
import { MomentService } from 'src/app/service/shared/moment/moment.service';

@Component({
  selector: 'app-tool-list-page',
  templateUrl: './tool-list-page.component.html',
  styleUrls: ['./tool-list-page.component.scss'],
})
export class ToolListPageComponent implements OnInit {
  defaultSortOrder = 'toolId,desc'

  constructor(
    private toolSettingsService: ToolSettingsService,
    private toolSnapshotService: ToolSnapshotService,
    /* tslint:disable-next-line variable-name */
    @Inject(DOCUMENT) public _document,
    public renderer2: Renderer2,
    private toolPermissionsService: ToolPermissionsService,
    private momentService: MomentService,
  ) { }

  data: TableData = {
    label: 'Tool',
    type: 'tools',
    editAccess: true,
    selectable: true,
    statusToggle: false,
    columns: [
      {
        name: 'toolId',
        label: 'Tool ID',
        property: 'toolId',
        sortable: true,
        columnStyles: { 'min-width': '3rem', 'width': '5rem', padding: '12px 8px' },
        headerStyles: { 'min-width': '3rem', 'width': '5rem', padding: '12px 8px' },
        columnClasses: { 'text-truncate': true },
        show: row => {
          return this.toolPermissionsService.isAllowed(row, 'tools', 'detailed_view');
        },
        link: row => {
          return [`/tools/edit/${row.id}`];
        },
      },
      {
        name: "inventoriedDate",
        label: 'Inv.',
        property: () => '',
        sortable: true,
        columnStyles: { 'min-width': '1rem', 'width': '4%', 'max-width': '2rem', padding: '12px 8px' },
        headerStyles: { 'min-width': '1rem', 'width': '4%', 'max-width': '2rem', padding: '12px 8px' },
        columnClasses: {
          'text-truncate': false, 'text-align': 'left',
          'text-overflow': 'ellipsis'
        },
        component: 'inventory-icon',
      },
      {
        name: 'tool_name',
        label: 'Tool Description',
        property: row => model(row, 'name'),
        sortable: true,
        columnStyles: {
          'max-width': '210px', 'white-space': 'inherit',
        },
        headerStyles: { 'min-width': '10rem', padding: '12px 12px 12px 4px' },
        columnClasses: { 'text-truncate': true, 'clamp-to-4': true },
      },
      {
        name: 'manufacturer',
        label: 'Manufacturer',
        property: row => model(row, 'manufacturer.name'),
        sortable: true,
        columnStyles: { 'max-width': '120px' },
        headerStyles: { 'min-width': '9rem' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'modelName',
        label: 'Model',
        property: row => model(row, 'description'),
        sortable: true,
        columnStyles: { 'max-width': '210px', 'white-space': 'inherit' },
        headerStyles: { 'min-width': '6rem' },
        columnClasses: { 'text-truncate': true, 'clamp-to-4': true },
        show: row => {
          return this.toolPermissionsService.isAllowed(row, 'tool_models', 'detailed_view');
        },
        link: row => {
          return [`/tools/models/edit/${model(row, 'id')}`];
        },
      },
      {
        name: 'category',
        label: 'Category',
        property: row => model(row, 'category.name'),
        sortable: true,
        columnStyles: {
          'min-width': '100px',
          'max-width': '7%',
          'white-space': 'inherit',
        },
        headerStyles: { 'min-width': '8rem' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'serialNumber',
        label: 'Serial #',
        property: 'serialNumber',
        sortable: true,
        columnStyles: { 'max-width': '110px' },
        headerStyles: { 'min-width': '8rem' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'lastAssignedDate',
        label: 'Last Assigned',
        property: row => assignment(row, 'assignedDate'),
        sortable: true,
        columnStyles: { 'max-width': '50px', padding: '12px 8px' },
        headerStyles: { 'max-width': '8.5rem', padding: '12px 8px' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'assignedTo',
        label: 'Assigned To',
        property: row => assignment(row, 'primaryAssignment'),
        sortable: true,
        columnStyles: { 'max-width': '110px', 'white-space': 'inherit' },
        headerStyles: { 'min-width': '10rem' },
        columnClasses: { 'text-truncate': false },
      },
      {
        name: 'responsibleParty',
        label: 'Responsible Party',
        property: row => {
          return assignment(row, 'responsibleParty') ? `${assignment(row, 'responsibleParty.employeeAlias')}` : '';
        },
        sortable: true,
        columnStyles: { 'max-width': '110px', 'white-space': 'inherit' },
        headerStyles: { 'min-width': '12rem' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'status',
        label: 'Status',
        property: row => assignment(row, 'type.name'),
        sortable: true,
        columnStyles: { 'max-width': '110px' },
        headerStyles: { 'min-width': '8rem', 'text-align': 'center' },
        component: 'tool-status',
      },
    ],
    rowActions: [
      {
        label: 'Transfer',
        actionId: 'transfer',
        imageUrl: '',
        iconName: 'far fa-exchange-alt',
        show: row => {
          return this.toolPermissionsService.isAllowed(row, 'tools', 'edit');
        },
      },
      {
        label: 'Copy',
        actionId: 'copy',
        imageUrl: '',
        iconName: 'far fa-clone',
        show: (row, meta) => {
          return this.toolPermissionsService.isAllowed(row, 'tools', 'edit');
        },
      },
      {
        label: 'Edit',
        actionId: 'edit',
        imageUrl: '',
        iconName: 'far fa-pen',
        show: (row, meta) => {
          return this.toolPermissionsService.isAllowed(row, 'tools', 'edit');
        },
      },
      {
        label: 'Delete',
        actionId: 'delete',
        imageUrl: '',
        iconName: 'far fa-trash',
        show: (row, meta) => {
          return this.toolPermissionsService.isAllowed(row, 'tools', 'admin');
        },
      },
    ],
    data: [],
    pagination: {},
    meta: {},
    tableClasses: 'tool-settings-table-for-toolpointe',
  };
  filters: Array<any> = [
    {
      label: 'Employee',
      type: 'select2',
      propertyName: 'scope[employee]',
      default: '',
      value: 'id',
      name: 'employeeAlias',
      model: 'employees',
      options: [],
      loadOptions: () => this.loadOptions('employees'),
    },
    {
      label: 'Job',
      type: 'autocomplete',
      propertyName: 'scope[job]',
      default: '',
      value: 'id',
      name: 'jobs',
      model: 'jobs',
      options: [],
      loadOptions: () => this.loadOptions('jobs'),
    },
    {
      label: 'Owner',
      type: 'select2',
      propertyName: 'scope[owner]',
      default: '',
      value: 'id',
      name: 'owner_name',
      model: 'owner',
      options: [],
      loadOptions: () => this.loadOptions('owner'),
    },
    {
      label: 'Shop Location',
      type: 'select2',
      propertyName: 'scope[shop]',
      default: '',
      value: 'id',
      name: 'name',
      model: 'shop-locations',
      options: [],
      loadOptions: () => this.loadOptions('shop-locations', { filter: 'enabled=1' }),
    },
    {
      label: 'Category',
      type: 'select2',
      propertyName: 'scope[category]',
      default: '',
      name: 'name',
      model: 'categories',
      options: [],
      loadOptions: () => this.loadOptions('categories'),
    },
    {
      label: 'Department',
      type: 'select2',
      propertyName: 'scope[department]',
      default: '',
      value: 'id',
      name: 'department_name',
      model: 'departments',
      options: [],
      loadOptions: () => this.loadOptions('departments'),
    },
  ];
  moreFilters: Array<any> = [
    {
      label: 'Asset Class',
      type: 'select2MultipleWithCheck',
      propertyName: 'scope[assetClass]',
      default: '',
      value: 'id',
      name: 'name',
      model: 'asset-classes',
      options: [],
      loadOptions: () => this.loadOptions('asset-classes'),
    },
    {
      label: 'Status',
      type: 'select2',
      propertyName: 'scope[status]',
      default: '',
      value: 'id',
      name: 'name',
      model: 'assignment_types',
      options: [
        { id: 1, name: 'Assigned', selectedByDefault: true },
        { id: 3, name: 'Available', selectedByDefault: true },
        { id: 2, name: 'In Repair' },
        { id: 4, name: 'Sold' },
        { id: 5, name: 'Missing' },
        { id: 6, name: 'Broken' },
        { id: 7, name: 'Stolen' },
        { id: 8, name: 'Retired' },
      ],
    },
    {
      label: 'Shop Origination',
      type: 'select2MultipleWithCheck',
      propertyName: 'scope[shopOriginationId]',
      default: '',
      value: 'name',
      name: 'name',
      model: 'shop_origination_id',
      options: [],
      loadOptions: () => this.loadOptions('shop-locations', { 'scope[hardShops]': true }),
    },
    {
      label: 'Vehicle Models Only',
      model: 'is_vehicle',
    },
  ];

  searchParams: ToolSearchParams = {
    page: 1,
    limit: 50,
    order: this.defaultSortOrder,
    embed: 'assignment,model,alert,vehicleDetail,toolInventory',
    searchFields: this.searchFields,
  };

  exportEmbedGroups = {
    default: 'assignment,model,vehicleDetail,shopLocation,toolInventory',
  };

  toolExportParams: ToolSearchParams = {
    embed: this.exportEmbedGroups['default'],
  };

  someData: any;

  snapshots: Array<any> = [];

  get attributes() {
    const attributes = [
      'id',
      'toolId',
      'initialMileageHours',
      'lastMileageHours',
      'description',
      'serialNumber',
      'model.id|name',
      'assignment.id|assignedDate|primaryAssignment|notes',
      'alert.id|message|enabled',
    ];
    return attributes.join(',');
  }

  get searchFields() {
    const fields = [
      'toolId',
      'description',
      'serialNumber',
      'model.name',
      'model.description',
      'model.category.name',
      'model.category.assetClass.name',
      'model.manufacturer.name',
      'assignment.assigned_date',
      'assignment.type.name',
      'assignment.job.name',
      'assignment.owner.owner_name',
      'assignment.shopLocation.name',
      'assignment.vendor.company_name',
      'assignment.employee.first_name',
      'assignment.employee.last_name',
      'assignment.employee.employee_alias',
      'assignment.responsibleParty.first_name',
      'assignment.responsibleParty.last_name',
      'assignment.responsibleParty.employee_alias',
      'vehicleDetail.license_plate',
      'vehicleDetail.fleet_vehicle_number',
      'assignment.facility.facility_name',
    ];
    return fields.join(',');
  }

  get exportAttributes() {
    const attributes = [
      'toolId',
      'initialMileageHours',
      'description',
      'serialNumber',
      'lastMileageHours',
      'model.manufacturer.name',
      'model.category.name',
      'model.category.assetClass.name',
      'notes',
      'purchDate',
      'purchPrice',
      'initialMileageHours',
    ];
    return attributes.join(',');
  }

  get exportFields() {
    const fields = [
      'toolId',
      'description',
      'serialNumber',
      'lastMileageHours',
      'model.manufacturer.name',
      'model.category.name',
      'model.category.assetClass.name',
      'vehicleDetail.license_plate',
      'notes',
      'assignment.assigned_to',
      'assignment.notes',
      'assignment.type.name',
      'assignment.assigned_date',
      'assignment.job.name',
      'assignment.owner.id',
      'assignment.vendor.company_name',
      'assignment.employee.employee_alias',
      'assignment.responsibleParty.employee_alias',
    ];
    return fields.join(',');
  }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.loadSelect2();
    this.loadTooltip();
  }

  loadSelect2() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadSelect2();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  loadTooltip() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadTooltip();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  getToolSnapShotService(event, row, rowRef, snapshotRef) {
    this.toolSnapshotService.fetchSnapshot(event, row, rowRef, snapshotRef);
  }

  loadOptions(key: string, options = {}): Promise<any> {
    // call the API for this endpoint 'key' and store result in Detail[key]
    return new Promise((resolve, reject) => {
      this.toolSettingsService.read[key](options).subscribe(
        res => {
          if (!Array.isArray(res.data)) res.data = res.data.data;

          if (key === 'departments')
            res.data = res.data.sort((a, b) => a.department_name.localeCompare(b.department_name));
          return resolve(res.data);
        },
        error => {
          console.error(error);
          return reject(error);
        },
      );
    });
  }

  formatToCurrency(amt: number) {
    let isNegative = false;
    if (amt < 0) {
      isNegative = true;
      amt *= -1;
    }

    const currency = (amt / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
    });
    return isNegative ? `($${currency})` : `$${currency}`;
  }
}
